/**
 * Truncate HTML by word count while preserving tags/structure
 * (same approach as HtmlContentWithReadMore used in hotel blocks).
 */
export function truncateHtmlByWords(html: string, maxWords: number): {
  html: string;
  truncated: boolean;
} {
  if (typeof document === "undefined") {
    return { html, truncated: false };
  }

  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").trim();
  const words = text ? text.split(/\s+/) : [];

  if (words.length <= maxWords) {
    return { html, truncated: false };
  }

  let currentWord = 0;
  let result = "";

  const walk = (node: Node) => {
    if (currentWord >= maxWords) return;

    if (node.nodeType === 3) {
      const nodeWords = node.textContent?.trim().split(/\s+/).filter(Boolean) || [];
      const remaining = maxWords - currentWord;
      if (nodeWords.length <= remaining) {
        result += node.textContent || "";
        currentWord += nodeWords.length;
      } else {
        const leadingSpace =
          node.textContent && /^\s/.test(node.textContent) ? " " : "";
        result +=
          leadingSpace + nodeWords.slice(0, remaining).join(" ") + "…";
        currentWord = maxWords;
      }
      return;
    }

    if (node.nodeType !== 1) return;

    const element = node as Element;
    const tag = element.tagName.toLowerCase();
    const attrs = Array.from(element.attributes)
      .map((attr) => ` ${attr.name}="${attr.value}"`)
      .join("");

    if (["br", "hr", "img"].includes(tag)) {
      result += `<${tag}${attrs} />`;
      return;
    }

    result += `<${tag}${attrs}>`;
    for (const child of Array.from(node.childNodes)) {
      walk(child);
    }
    result += `</${tag}>`;
  };

  for (const child of Array.from(div.childNodes)) {
    walk(child);
  }

  return { html: result, truncated: true };
}

export function countWordsInHtml(html: string): number {
  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, " ").trim().split(/\s+/).filter(Boolean)
      .length;
  }
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").trim();
  return text ? text.split(/\s+/).length : 0;
}
